import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ChevronDown, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');
  const confirmPassword = watch('confirm_password');

  // Available roles
  const availableRoles = [
    { id: 'is_doctor', label: 'Doctor' },
    { id: 'is_patient', label: 'Patient' },
    { id: 'is_clinician', label: 'Clinician' },
    { id: 'is_superuser', label: 'Super User' }
  ];

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Register';
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if passwords match
  useEffect(() => {
    if (password && confirmPassword && password === confirmPassword) {
      setPasswordsMatch(true);
    } else {
      setPasswordsMatch(false);
    }
  }, [password, confirmPassword]);



  const toggleRole = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Add selected roles to the form data
      const formData = {
        ...data,
        is_doctor: selectedRoles.includes('is_doctor'),
        is_patient: selectedRoles.includes('is_patient'),
        is_clinician: selectedRoles.includes('is_clinician'),
        is_superuser: selectedRoles.includes('is_superuser'),
        is_active: true // Set to true by default as requested
      };



      await authService.register(formData);
      toast.success('Registration successful! Please check your email to verify your account.');
      navigate('/auth/login');
    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
      <div className="w-[45%] relative overflow-hidden h-[115vh]">
        <img
          src="/login_side_image.png"
          alt="Healthcare Professional"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right Side - Registration Form (55%) */}
      <div className="w-[55%] flex items-start justify-center bg-white pt-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-4">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-blue-600 mb-3">
              Welcome To
            </h1>
            
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-3">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-14 w-auto"
              />
            </div>
            
            {/* Sign In Link */}
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/auth/login"
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                Sign in here.
              </Link>
            </p>
          </div>

          {/* Registration Form */}
          <div className="space-y-4">
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {/* First Name and Last Name - Two Columns */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name:
                  </label>
                  <input
                    id="first_name"
                    type="text"
                    {...register('first_name', {
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters',
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="First name"
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name:
                  </label>
                  <input
                    id="last_name"
                    type="text"
                    {...register('last_name', {
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters',
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Last name"
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-xs text-red-600">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email Field - Full Width */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email:
                </label>
                <input
                  id="email"
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Password and Confirm Password - Two Columns */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password:
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-2 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password:
                  </label>
                  <div className="relative">
                    <input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register('confirm_password', {
                        required: 'Please confirm your password',
                        validate: (value) =>
                          value === password || 'Passwords do not match',
                      })}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-2 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                    {/* Animated Green Tick */}
                    {passwordsMatch && (
                      <div className="absolute inset-y-0 right-6 flex items-center">
                        <Check className="h-4 w-4 text-green-500 animate-pulse" />
                      </div>
                    )}
                  </div>
                  {errors.confirm_password && (
                    <p className="mt-1 text-xs text-red-600">{errors.confirm_password.message}</p>
                  )}
                </div>
              </div>

              {/* Roles Dropdown - Full Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Roles:
                </label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between text-sm"
                  >
                    <span className={selectedRoles.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                      {selectedRoles.length > 0 
                        ? selectedRoles.map(roleId => 
                            availableRoles.find(r => r.id === roleId)?.label
                          ).join(', ')
                        : 'Select roles'
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                      {availableRoles.map((role) => (
                        <label
                          key={role.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.id)}
                            onChange={() => toggleRole(role.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{role.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {selectedRoles.length === 0 && (
                  <p className="mt-1 text-xs text-red-600">Please select at least one role</p>
                )}
              </div>



              {/* Create Account Button */}
              <button
                type="submit"
                disabled={isLoading || selectedRoles.length === 0}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage; 